import React, { useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { ACADEMY_CATEGORIES, ACADEMY_FUTURE_MODULES } from "@/lib/academy";
import {
  GraduationCap, Sparkles, Eye, AlertTriangle, Target, CheckCircle2,
  Lock,
  Heart, Droplet, Wind, Snowflake, Droplets, Layers, Flame,
  Layers3, Circle, TrendingDown,
} from "lucide-react";

const ICONS = {
  Heart, Droplet, Wind, Snowflake, Droplets, Layers, Flame,
  Layers3, Circle, TrendingDown,
};

const TONE_STYLES = {
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  amber:   { bg: "bg-amber-50",   text: "text-amber-800",   border: "border-amber-200" },
  sky:     { bg: "bg-sky-50",     text: "text-sky-700",     border: "border-sky-200" },
  slate:   { bg: "bg-slate-50",   text: "text-slate-700",   border: "border-slate-200" },
  orange:  { bg: "bg-orange-50",  text: "text-orange-800",  border: "border-orange-200" },
  stone:   { bg: "bg-stone-50",   text: "text-stone-700",   border: "border-stone-200" },
  rose:    { bg: "bg-rose-50",    text: "text-rose-700",    border: "border-rose-200" },
  zinc:    { bg: "bg-zinc-50",    text: "text-zinc-700",    border: "border-zinc-200" },
  violet:  { bg: "bg-violet-50",  text: "text-violet-700",  border: "border-violet-200" },
};

function CategoryNav({ categories, activeId, onSelect }) {
  return (
    <nav
      className="rounded-2xl border border-[#E5E5E0] bg-white p-3 sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto"
      data-testid="academy-nav"
    >
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] font-medium px-3 py-2">
        Libreria visiva
      </p>
      <ul className="space-y-1">
        {categories.map((c) => {
          const Icon = ICONS[c.accent] || Sparkles;
          const active = c.id === activeId;
          const tone = TONE_STYLES[c.tone] || TONE_STYLES.stone;
          return (
            <li key={c.id}>
              <button
                onClick={() => onSelect(c.id)}
                data-testid={`academy-nav-${c.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
                  active
                    ? "bg-[#1A1A1A] text-white"
                    : "hover:bg-[#FAF8F4] text-[#1A1A1A]"
                }`}
              >
                <span
                  className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border ${
                    active ? "bg-white/10 border-white/20 text-[#C5A059]" : `${tone.bg} ${tone.border} ${tone.text}`
                  }`}
                >
                  <Icon className="w-4 h-4" />
                </span>
                <span className="text-sm font-medium leading-tight">{c.title}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section className="rounded-2xl border border-[#E5E5E0] bg-white p-6" data-testid={testid}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-[#FAF6EE] flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#C5A059]" />
        </div>
        <h3 className="font-serif-display text-xl text-[#1A1A1A]">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function BulletList({ items, bullet = "›", tone = "gold" }) {
  const color = tone === "danger" ? "text-rose-500" : "text-[#C5A059]";
  return (
    <ul className="space-y-2.5">
      {items.map((b, i) => (
        <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-[#1A1A1A]">
          <span className={`${color} mt-1 shrink-0`}>{bullet}</span>
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

export default function Academy() {
  const [activeId, setActiveId] = useState(ACADEMY_CATEGORIES[0].id);
  const active = useMemo(
    () => ACADEMY_CATEGORIES.find((c) => c.id === activeId) || ACADEMY_CATEGORIES[0],
    [activeId],
  );
  const Icon = ICONS[active.accent] || Sparkles;
  const tone = TONE_STYLES[active.tone] || TONE_STYLES.stone;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto animate-fade-up" data-testid="academy-page">
        {/* Hero / intro */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-[#1A1A1A] flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-[#C5A059]" />
            </div>
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-medium">
                Formazione base
              </p>
              <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1A1A1A] leading-tight">
                AI TricoAcademy<span className="text-[#C5A059]">™</span>
              </h1>
            </div>
          </div>
          <p className="text-[#737373] max-w-2xl leading-relaxed">
            Una libreria visiva pensata per aiutarti a leggere correttamente le immagini
            da tricocamera. Conoscere l’aspetto delle principali condizioni della cute
            ti permette di interpretare meglio l’analisi AI, ridurre errori e costruire
            protocolli davvero su misura.
          </p>

          <div className="mt-6 rounded-2xl bg-[#FAF6EE] border border-[#E5E5E0] px-6 py-5 flex items-start gap-4 max-w-3xl">
            <Sparkles className="w-5 h-5 text-[#C5A059] mt-0.5 shrink-0" />
            <p className="text-[#1A1A1A] leading-relaxed italic">
              “AI TricoProtocol<span className="text-[#C5A059]">™</span> è uno strumento
              avanzato di supporto, ma la qualità della diagnosi parte sempre da una
              corretta lettura visiva.”
            </p>
          </div>
        </div>

        {/* Main grid */}
        <div className="grid lg:grid-cols-[280px_1fr] gap-8">
          <CategoryNav
            categories={ACADEMY_CATEGORIES}
            activeId={activeId}
            onSelect={setActiveId}
          />

          <div className="space-y-6" data-testid={`academy-card-${active.id}`}>
            {/* Card header */}
            <div className="rounded-2xl border border-[#E5E5E0] bg-white overflow-hidden">
              <div className={`px-8 py-6 ${tone.bg} border-b ${tone.border}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl bg-white border ${tone.border} flex items-center justify-center shrink-0`}>
                    <Icon className={`w-5 h-5 ${tone.text}`} />
                  </div>
                  <div>
                    <p className={`text-xs uppercase tracking-[0.18em] ${tone.text} font-medium mb-1`}>
                      Scheda formativa
                    </p>
                    <h2 className="font-serif-display text-3xl text-[#1A1A1A] leading-tight">
                      {active.title}
                    </h2>
                    {active.subtitle && (
                      <p className="text-sm text-[#737373] mt-1">{active.subtitle}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="px-8 py-8 max-w-3xl mx-auto">
                <p className="text-[15px] sm:text-base text-[#1A1A1A] leading-relaxed text-center">
                  {active.description}
                </p>
              </div>
            </div>

            {/* Structured sections */}
            <div className="grid md:grid-cols-2 gap-5">
              <Section icon={Eye} title="Cosa osservare" testid="academy-cosa-osservare">
                <BulletList items={active.cosa_osservare} />
              </Section>

              <Section icon={AlertTriangle} title="Errori comuni" testid="academy-errori-comuni">
                <BulletList items={active.errori_comuni} bullet="×" tone="danger" />
              </Section>

              <Section icon={Target} title="Impatto sulla diagnosi" testid="academy-impatto">
                <p className="text-[15px] text-[#1A1A1A] leading-relaxed">
                  {active.impatto_diagnosi}
                </p>
              </Section>

              <Section icon={CheckCircle2} title="Indicazioni operative" testid="academy-indicazioni">
                <BulletList items={active.indicazioni_operative} bullet="✓" />
              </Section>
            </div>
          </div>
        </div>

        {/* Future modules (locked) */}
        <div className="mt-16 pt-10 border-t border-[#E5E5E0]">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="w-4 h-4 text-[#737373]" />
            <p className="text-xs uppercase tracking-[0.2em] text-[#737373] font-medium">
              In arrivo nei prossimi aggiornamenti
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4" data-testid="academy-future-modules">
            {ACADEMY_FUTURE_MODULES.map((m) => (
              <div
                key={m.id}
                className="rounded-2xl border border-dashed border-[#E5E5E0] bg-[#FAFAFA] p-6 relative"
              >
                <span className="absolute top-4 right-4 text-[10px] tracking-[0.18em] uppercase text-[#737373] bg-white border border-[#E5E5E0] px-2 py-1 rounded-full">
                  Coming soon
                </span>
                <h4 className="font-serif-display text-xl text-[#1A1A1A] mb-2">{m.title}</h4>
                <p className="text-sm text-[#737373] leading-relaxed">{m.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
